import {
  ChangeDetectionStrategy,
  Component,
  TemplateRef,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ModalComponent } from '../modal-component/modal.component';
import { ButtonComponent } from '../button/button.component';

export type OkType = 'primary' | 'success' | 'warn' | 'orange' | 'white';

export interface ConfirmationData {
  okText?: string;
  cancelText?: string;
  okType?: OkType;
  confirmContent?: string | TemplateRef<unknown>;
  headerContent?: string | TemplateRef<unknown>;
  confirmClass?: string;
  hideCancelBtn?: boolean;
  showHeader?: boolean;
  showCloseIcon?: boolean;
  noDivider?: boolean;
  title?: string;
  contextData?: unknown;
}

@Component({
  selector: 'lbt-confirmation',
  standalone: true,
  templateUrl: `confirmation.component.html`,
  styleUrls: [`./confirmation.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogClose, ModalComponent, NgTemplateOutlet, ButtonComponent, NgClass],
})
export class ConfirmationComponent {
  protected readonly data = inject<ConfirmationData | undefined>(MAT_DIALOG_DATA, {
    optional: true,
  });

  disableConfirm = input<boolean | undefined>(undefined);
  confirmContent = input<string | TemplateRef<unknown> | undefined>(undefined);
  headerContent  = input<string | TemplateRef<unknown> | undefined>(undefined);
  okText         = input<string | undefined>(undefined);
  cancelText     = input<string>('Cancel');
  okType         = input<OkType | undefined>(undefined);
  confirmClass   = input<string | undefined>(undefined);
  hideCancelBtn  = input<boolean | undefined>(undefined);
  showHeader     = input<boolean>(false);
  showCloseIcon  = input<boolean>(true);
  noDivider      = input<boolean>(false);
  title          = input<string | undefined>(undefined);

  confirm = output<unknown>();
  cancel  = output<unknown>();

  private readonly DEFAULT_OK_TYPE: OkType = 'warn';

  readonly resolvedOkText = computed(() => this.okText() ?? this.data?.okText);
  readonly resolvedCancelText = computed(
    () => this.cancelText() ?? this.data?.cancelText
  );
  readonly resolvedOkType = computed<OkType>(() => {
    return this.okType() ?? this.data?.okType ?? this.DEFAULT_OK_TYPE;
  });

  readonly resolvedHideCancelBtn = computed<boolean | undefined>(() => {
    return this.hideCancelBtn() ?? this.data?.hideCancelBtn;
  });
  readonly resolvedShowHeader = computed<boolean>(() => {
    const fromInput = this.showHeader(); // boolean input with default false
    return fromInput ?? this.data?.showHeader ?? false;
  });
  readonly resolvedShowCloseIcon = computed<boolean>(() => {
    return this.showCloseIcon() ?? this.data?.showCloseIcon ?? true;
  });
  readonly resolvedNoDivider = computed<boolean>(() => {
    return this.noDivider() ?? this.data?.noDivider ?? false;
  });
  readonly resolvedTitle = computed(() => this.title() ?? this.data?.title);
  readonly resolvedConfirmClass = computed(
    () => this.confirmClass() ?? this.data?.confirmClass
  );

  private readonly resolvedConfirmContent = computed<
    string | TemplateRef<unknown> | undefined
  >(() => this.confirmContent() ?? this.data?.confirmContent);

  private readonly resolvedHeaderContent = computed<
    string | TemplateRef<unknown> | undefined
  >(() => this.headerContent() ?? this.data?.headerContent);

  readonly confirmContentTmpl = computed<TemplateRef<unknown> | undefined>(() => {
    const c = this.resolvedConfirmContent();
    return c instanceof TemplateRef ? c : undefined;
  });

  readonly headerContentTmpl = computed<TemplateRef<unknown> | undefined>(() => {
    const h = this.resolvedHeaderContent();
    return h instanceof TemplateRef ? h : undefined;
  });

  readonly confirmContentHtml = computed<string | undefined>(() => {
    const c = this.resolvedConfirmContent();
    return typeof c === 'string' ? c.replace(/\n/g, '<br/>') : undefined;
  });

  readonly headerContentHtml = computed<string | undefined>(() => {
    const h = this.resolvedHeaderContent();
    return typeof h === 'string' ? h.replace(/\n/g, '<br/>') : undefined;
  });

  onConfirm(payload?: unknown) {
    this.confirm.emit(payload);
  }

  onCancel(payload?: unknown) {
    this.cancel.emit(payload);
  }
}
